import { useState } from 'react';
import styled from 'styled-components';
import { Ace } from 'ace-builds';
import AceEditor, { IAceEditorProps } from 'react-ace';
import ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/mode-graphqlschema';
import { IconButton, Tooltip } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CodeIcon from '@mui/icons-material/Code';
import { format as formatSql } from 'sql-formatter';
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { ReactComponent as VIcon } from '../../assets/v.svg';
import { Flex } from '../Flex';
import SvgIcon from '../SvgIcon';

// Configure ace editor base path for proper worker loading
// This prevents "Failed to load worker" errors in development
ace.config.set(
  'basePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'modePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'themePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'workerPath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);

const Buttons = styled(Flex)`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
`;

const formatCode = (code: string, mode?: string): string => {
  if (!code || !code.trim()) return code;

  try {
    if (mode === 'sql') {
      return formatSql(code, {
        language: 'sql',
        tabWidth: 2,
        keywordCase: 'upper',
      });
    }
    return code;
  } catch {
    return code;
  }
};

type Props = Omit<IAceEditorProps, 'theme'> & {
  mode?: string;
  height?: string;
  fontSize?: number;
  grayedOut?: boolean;
  hideLineNumbers?: boolean;
};

const CodeEditor = styled(AceEditor).attrs(
  ({
    theme,
    fontSize = 13,
    width = '100%',
    showPrintMargin = false,
    grayedOut,
    hideLineNumbers,
    mode = 'text',
    ...props
  }: Props & { theme: any }) => ({
    // Workaround to the fact that the AceEditor accepts a theme prop itself
    theme: grayedOut ? undefined : 'monokai',
    colorTheme: theme,
    fontSize,
    width,
    showPrintMargin,
    grayedOut,
    onLoad: (editor: Ace.Editor) => {
      editor.renderer.setScrollMargin(10, 10, 0, 0);
      if (hideLineNumbers) {
        editor.renderer.setShowGutter(false);
        editor.renderer.setPadding(10);
      }
    },
    mode,
    ...props,
  }),
)`
  border-radius: 8px;

  * {
    font-family: 'SFMono-Regular', 'Menlo', 'Consolas', 'Liberation Mono',
      'Courier New', monospace;
  }

  .error-marker {
    position: absolute;
    border-bottom: 1px solid
      ${({ colorTheme }) => colorTheme.palette.common.red2};
    border-radius: 0;
    min-width: 3px;
    background-color: ${({ colorTheme }) => colorTheme.palette.common.red2};
    opacity: 0.3;
    width: 100% !important;
    left: 0 !important;
  }

  .ace_error {
    background-image: none;
  }

  .ace_gutter-cell.ace_error {
    background-color: ${({ colorTheme }) => colorTheme.palette.common.red2};
    color: ${({ colorTheme }) => colorTheme.palette.common.white};
  }

  ${({ readOnly }) =>
    readOnly &&
    `
      .ace_cursor {
          opacity: 0
      }
      .ace_active-line {
        background: none !important;
      }
      .ace_gutter-active-line {
        background: none !important;
      }
`};

  ${({ grayedOut }) =>
    grayedOut &&
    `
      &.ace-tm {
        opacity: 0.5;
        filter: grayscale(100%) brightness(85%);
      }

      .ace_content {
        background-color: #f2f2f2 !important; 
      }

      .ace_gutter {
        background: #e0e0e0 !important; 
        color: #a0a0a0 !important; 
      }

      .ace_cursor {
        display: none !important; 
      }

      .ace_marker-layer .ace_selection {
        background: rgba(200, 200, 200, 0.3) !important; 
      }
`};
`;

const CopyTooltip = ({ isCopied }: { isCopied: boolean }) => (
  <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
    {isCopied && (
      <SvgIcon strokeColor="white">
        <VIcon />
      </SvgIcon>
    )}
    {isCopied ? 'Copied!' : 'Copy'}
  </Flex>
);

const CodeEditorWrapper = (props: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const trimmedValue = props.value?.trim() || '';
  const [isFormatted, setIsFormatted] = useState(true);

  const displayValue =
    isFormatted && props.readOnly
      ? formatCode(trimmedValue, props.mode)
      : props.readOnly
      ? trimmedValue
      : props.value || '';

  const handleCopy = () => {
    navigator.clipboard.writeText(displayValue);
    setIsCopied(true);
  };

  const showFormatButton = props.readOnly && props.mode === 'sql';

  // When height is provided, exclude maxLines and minLines to avoid type conflicts
  const { height, maxLines, minLines, ...editorProps } = props;

  const sizeProps = height
    ? { height }
    : {
        maxLines: maxLines || Infinity,
        minLines: minLines || 1,
      };

  return (
    <Flex
      sx={{
        position: 'relative',
        height: props.height,
        width: props.width || '100%',
      }}
    >
      <CodeEditor {...editorProps} {...sizeProps} value={displayValue} />

      <Buttons>
        {showFormatButton && (
          <Tooltip title={isFormatted ? 'Raw' : 'Beautify'}>
            <IconButton onClick={() => setIsFormatted(!isFormatted)}>
              {isFormatted ? (
                <CodeIcon sx={{ fontSize: 16, color: 'white' }} />
              ) : (
                <AutoFixHighIcon sx={{ fontSize: 16, color: 'white' }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={<CopyTooltip isCopied={isCopied} />}>
          <IconButton
            onClick={handleCopy}
            onMouseLeave={() => setIsCopied(false)}
          >
            <SvgIcon width={16} strokeColor="white">
              <CopyIcon />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      </Buttons>
    </Flex>
  );
};

export { CodeEditor as BaseCodeEditor };

export default CodeEditorWrapper;
